const SLACK_MEMBER_ID = /^[UW][A-Z0-9]{8,}$/i;
const SLACK_MEMBER_MENTION = /^<@([UW][A-Z0-9]{8,})>$/i;

export const MAX_SLACK_MEMBER_REFERENCE_LENGTH = 512;
export const MAX_SLACK_MEMBER_ID_LENGTH = 128;

function normalizedSlackMemberId(value: string): string | null {
  return SLACK_MEMBER_ID.test(value) && value.length <= MAX_SLACK_MEMBER_ID_LENGTH
    ? value.toUpperCase()
    : null;
}

/**
 * Normalize the Slack references an admin can copy without making them learn
 * Slack's internal URL shape: a raw member id, a rendered mention, or a Slack
 * profile URL. Channel/team ids and lookalike URLs fail closed.
 */
export function normalizeSlackMemberReference(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const reference = value.trim();
  if (!reference || reference.length > MAX_SLACK_MEMBER_REFERENCE_LENGTH) {
    return null;
  }

  const rawMemberId = normalizedSlackMemberId(reference);
  if (rawMemberId) return rawMemberId;

  const mention = reference.match(SLACK_MEMBER_MENTION);
  if (mention) return normalizedSlackMemberId(mention[1]);

  let url: URL;
  try {
    url = new URL(reference);
  } catch {
    return null;
  }
  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.port ||
    (url.hostname !== "slack.com" && !url.hostname.endsWith(".slack.com"))
  ) {
    return null;
  }

  const memberIds = url.pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch {
        return "";
      }
    })
    .map(normalizedSlackMemberId)
    .filter((segment): segment is string => segment !== null);

  return memberIds.length === 1 ? memberIds[0] : null;
}
